import React, { useEffect, useState, useCallback } from "react";
import moment from "moment";
import { ToastContainer } from "react-toastify";
import { Paper, Box } from "@mui/material";

import { usePermissions } from "../../hooks/usePermissions";
import { useContentLanguage } from "../../hooks/useContentLanguage";
import { PageHeader } from "../../components/PageHeader";
import { LanguageTabs } from "../../components/LanguageTabs";

import { ItemsList } from "./ItemsList";
import { defaultInputValue } from "./NewsForm";
import { NewsForm } from "./NewsForm";
import { DeletionModal } from "./DeletionModal";
import { Loader } from "./Loader";
import { getItems } from "./getItems";
import { deleteItem } from "./deleteItem";

const News = () => {
  // Permission-based access control
  const { canCreate, canEdit, canDelete } = usePermissions("/news");
  const [lang, setLang] = useContentLanguage();
  const [page, setPage] = useState(1);

  const [countPerPage, setCountPerPage] = useState(10);
  const [count, setCount] = useState(0);
  const [data, setData] = useState<any[]>([]);

  const [inputValue, setInputValue] = useState(defaultInputValue);
  const [errors, setErrors] = useState(defaultInputValue);

  const [loadingData, setLoadingData] = useState(false);
  const [isEditing, setEditing] = useState(false);

  const [idForNewsEdit, setIdForNewsEdit] = useState("");
  const [idForNewsDelete, setIdForNewsDelete] = useState("");

  const [search, setSearch] = useState("");
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [showDeletionModal, setDeletionModal] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);

  const handleOnInputFieldChange = (e: any) => {
    const { name, value } = e.target;
    setInputValue({ ...inputValue, [name]: value });
    setErrors({ ...errors, [name]: "" });
  };

  const handleClose = () => {
    setDeletionModal(false);
    setFormModalOpen(false);
    setEditing(false);
    setErrors(defaultInputValue);
    setInputValue(defaultInputValue);
  };

  const handleGetNewsDetails = useCallback(() => {
    getItems({
      page,
      countPerPage,
      search,
      setLoadingData,
      setPage,
      setCountPerPage,
      setData,
      setCount,
    });
  }, [page, countPerPage, search]);

  useEffect(() => {
    handleGetNewsDetails();
  }, [handleGetNewsDetails]);

  const handleItemDeletionInModal = () =>
    deleteItem({
      idForNewsDelete,
      page,
      countPerPage,
      search,
      setLoadingData,
      setPage,
      setCountPerPage,
      setData,
      setCount,
      setDeletionModal,
    });

  const onDeleteItem = (id: string) => {
    setIdForNewsDelete(id);
    setDeletionModal(true);
  };

  return (
    <>
      <ToastContainer />
      <Paper sx={{ p: 1 }}>
        <Box sx={{ p: 2, mb: 2 }}>
          <PageHeader
            title="News und Blogs"
            onSearch={(val) => { setSearch(val); setPage(1); }}
            onAdd={canCreate ? () => setFormModalOpen(true) : undefined}
          />

          <LanguageTabs value={lang} onChange={setLang} />

          {loadingData ? (
            <Loader />
          ) : (
            <ItemsList
              items={data}
              onDelete={onDeleteItem}
              lang={lang}
              onEdit={(item) => {
                setEditing(true);
                setFormModalOpen(true);
                setIdForNewsEdit(item._id);
                const mediaEn = Array.isArray(item.mediaUrl)
                  ? item.mediaUrl[0] || ""
                  : (item.mediaUrl as string) || "";
                const mediaGer = Array.isArray(item.mediaUrl_ger)
                  ? item.mediaUrl_ger[0] || ""
                  : (item.mediaUrl_ger as string) || "";
                setInputValue({
                  newsName: item.newsName,
                  newsName_ger: item.newsName_ger || "",
                  newsDescription: item.newsDescription,
                  newsDescription_ger: item.newsDescription_ger || "",
                  date: moment(item.date).format("YYYY-MM-DD"),
                  file: mediaEn,
                  file_ger: mediaGer,
                });
              }}
              canEdit={canEdit}
              canDelete={canDelete}
              page={page}
              count={count}
              rowsPerPage={countPerPage}
              onPageChange={setPage}
              onRowsPerPageChange={setCountPerPage}
            />
          )}
        </Box>
      </Paper>

      <DeletionModal
        showDeletionModal={showDeletionModal}
        setDeletionModal={setDeletionModal}
        handleClose={handleClose}
        handlerDeletion={handleItemDeletionInModal}
      />

      <NewsForm
        isOpen={formModalOpen}
        loading={imageUploading}
        isEditing={isEditing}
        editData={inputValue}
        idForNewsEdit={idForNewsEdit}
        onClose={handleClose}
        setEditing={setEditing}
        setLoading={setImageUploading}
        setModalOpen={setFormModalOpen}
        getNewsDetails={handleGetNewsDetails}
        handleOnInputFieldChange={handleOnInputFieldChange}
      />
    </>
  );
};

export default News;
